<!-- src/shared/components/logos/DefaultLogo.vue -->

<script setup lang="ts">
  import { resolveProductName } from '@/shared/constants/brand';
  import { useBootstrapStore } from '@/shared/stores/bootstrapStore';
  import { type LogoConfig } from '@/types/ui/layouts';
  import { computed, onMounted, onUnmounted, ref } from 'vue';

  /**
   * Props for controlling logo appearance
   */
  const props = withDefaults(
    defineProps<LogoConfig & { isColonelArea?: boolean }>(),
    {
      size: 64,
      href: '/',
      isColonelArea: false,
    }
  );

  const bootstrapStore = useBootstrapStore();
  const alternitaLogoSrc = '/dist/assets/Alternita_Logo_rgb.svg';
  const isDesktop = ref(false);

  /**
   * Brand-aware aria-label. Falls back to the neutral-safe product name
   * (a generic "Secure Links") via the shared `resolveProductName` helper when
   * bootstrap config has not provided a brand name. Never defaults to OTS
   * branding — keeps private-label deployments neutral (#3048 / #3049).
   *
   * Resolves through the shared helper directly rather than
   * `identityStore.productName` so this app-wide fallback mark stays
   * lightweight: it needs only the product-name string, not the identity
   * store's watchers / i18n init. (Same reasoning as `usePageTitle`, which also
   * resolves via the helper.) The helper is still the single source of truth for
   * the fallback, so this cannot drift from the other surfaces.
   */
  const ariaLabel = computed(
    () => props.ariaLabel || resolveProductName(bootstrapStore.brand_product_name)
  );

  const updateLogoSize = () => {
    isDesktop.value = window.matchMedia('(min-width: 768px)').matches;
  };

  const logoHeight = computed(() => (isDesktop.value ? 84 : 64));

  onMounted(() => {
    updateLogoSize();
    window.addEventListener('resize', updateLogoSize);
  });

  onUnmounted(() => {
    window.removeEventListener('resize', updateLogoSize);
  });
</script>

<template>
  <div class="flex items-center gap-3">
    <a
      :href="props.href"
      class="flex items-center !outline-none !ring-0 !shadow-none focus:!outline-none focus:!ring-0 focus:!shadow-none focus-visible:!outline-none focus-visible:!ring-0 focus-visible:!shadow-none"
      style="outline: none !important; box-shadow: none !important;">
      <img
        :src="alternitaLogoSrc"
        :alt="ariaLabel"
        class="block h-auto shrink-0 !outline-none !ring-0 !shadow-none"
        :style="{
          height: `${logoHeight}px`,
          width: 'auto',
          outline: 'none',
          border: '0',
          boxShadow: 'none',
        }" />
    </a>
  </div>
</template>
